import React, { useContext, useEffect, useState } from "react";
import { TbCalendarPlus } from "react-icons/tb";
import ssIcon from "../../assets/images/SS.svg";
import menIcon from "../../assets/images/men.svg";
import CSIcon from "../../assets/images/CS.svg";
import { Language } from "../../App";
import MentorMenteesModal from "./MentorMenteesModal";

// `label` here is only the English fallback — statLabels (computed inside
// the component, where `lang` is available) is what actually renders.
// key: read straight from mr_home_data (see getHomeStats below). new_request
// is wired to the same-named field already present in that response —
// the other three keep the fields they already had, just relabeled to match
// the new 4-card design. Scheduled/Completed/Mentees share the SS.svg image
// icon instead of a react-icon component.
const STAT_CARDS = [
  { id: 1, label: "New requests", key: "new_request", icon: TbCalendarPlus },
  { id: 2, label: "Scheduled sessions", key: "scheduled_sessions", iconSrc: ssIcon },
  { id: 3, label: "Completed Sessions", key: "completed_sessions", iconSrc: CSIcon },
  { id: 4, label: "Mentees", key: "mentees", iconSrc: menIcon },
];

// MentorshipGrid.jsx's /user/mentor/home fetch stashes the whole response
// (including these counts) under this key — read from there instead of
// hitting the API again from here.
function getHomeStats() {
  try {
    const data = JSON.parse(localStorage.getItem("mr_home_data"));
    return {
      new_request: data?.new_request ?? 0,
      scheduled_sessions: data?.scheduled_sessions ?? 0,
      completed_sessions: data?.completed_sessions ?? 0,
      mentees: data?.mentees ?? 0,
    };
  } catch (e) {
    return { new_request: 0, scheduled_sessions: 0, completed_sessions: 0, mentees: 0 };
  }
}

// mr_home_data isn't written until MentorshipGrid.jsx's fetch resolves —
// this key's mere presence is what tells us that first fetch has actually
// happened at least once.
function hasHomeDataLoaded() {
  return localStorage.getItem("mr_home_data") !== null;
}

// These are the doctor's own mentor-dashboard numbers (sessions they've run,
// mentees they've guided) — meaningless for a plain mentee account, so the
// whole card row is skipped for one. Same login-set flag MentorshipRequests.jsx
// uses to decide whether the mentor/mentee switch even exists.
function isMentor() {
  return localStorage.getItem("mr_is_mentor") === "true";
}

// New requests and scheduled sessions both live under MentorshipRequests.jsx's
// "active" tab (server-side bucket) — only completed sessions have their own
// "completed" tab. Mentees isn't a tab at all; handled separately by opening
// MentorMenteesModal.jsx instead of dispatching this.
const STAT_TAB_TARGET = {
  new_request: "active",
  scheduled_sessions: "active",
  completed_sessions: "completed",
};

function StatCardShimmer() {
  return (
    <div className="mentorship-stats-card d-flex align-items-center bg-white flex-grow-1">
      <div
        className="mentorship-stats-shimmer-block mentorship-stats-icon flex-shrink-0"
        style={{ borderRadius: "12px" }}
      />
      <div className="flex-grow-1">
        <div className="mentorship-stats-shimmer-block" style={{ width: "40px", height: "20px", borderRadius: "4px", marginBottom: "8px" }} />
        <div className="mentorship-stats-shimmer-block" style={{ width: "80%", height: "14px", borderRadius: "4px" }} />
      </div>
    </div>
  );
}

function MentorshipStats() {
  const { lang } = useContext(Language);
  const [dataReady, setDataReady] = useState(hasHomeDataLoaded);
  const [isMenteesModalOpen, setIsMenteesModalOpen] = useState(false);

  // MentorshipGrid.jsx dispatches this the moment mr_home_data is written —
  // shows the shimmer until then instead of just rendering zeros.
  useEffect(() => {
    if (dataReady) return undefined;
    const handleUpdate = () => setDataReady(true);
    window.addEventListener("mrHomeDataUpdated", handleUpdate);
    return () => window.removeEventListener("mrHomeDataUpdated", handleUpdate);
  }, [dataReady]);

  // isMentor() below reads localStorage fresh every render, but a plain
  // localStorage write (services/api.js's syncMentorApprovalFlag, which can
  // resolve after this component has already mounted/rendered) doesn't
  // itself trigger a re-render — this tick just forces one when that
  // happens, so a mid-session approval actually flips this card row on
  // without needing a manual page reload.
  const [, forceMentorTick] = useState(0);
  useEffect(() => {
    const handleSync = () => forceMentorTick((n) => n + 1);
    window.addEventListener("mrMentorStatusSynced", handleSync);
    return () => window.removeEventListener("mrMentorStatusSynced", handleSync);
  }, []);

  if (!isMentor()) return null;

  const stats = getHomeStats();
  const statLabels = {
    new_request: lang?.mentorship_new_requests_label || "New requests",
    scheduled_sessions: lang?.mentorship_scheduled_sessions_label || "Scheduled sessions",
    completed_sessions: lang?.mentorship_completed_sessions_label || "Completed Sessions",
    mentees: lang?.mentorship_mentees_label || "Mentees",
  };

  return (
    <div className="mentorship-stats-row my-3">
      <style>{`
        .mentorship-stats-shimmer-block {
          background: linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%);
          background-size: 200% 100%;
          animation: mrShimmerWave 1.6s ease-in-out infinite;
        }
        @keyframes mrShimmerWave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .mentorship-stats-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .mentorship-stats-card {
          border: 1px solid #D7E0EA;
          border-radius: 20px;
          min-width: 180px;
          padding: 1rem;
          gap: 12px;
        }
        .mentorship-stats-icon {
          width: 44px;
          height: 44px;
        }
        /* Below this the 4 cards were each stacking full-width, one per
           row — switch to a fixed 2-column grid instead, with the cards
           themselves shrunk to fit two side by side. */
        @media (max-width: 576px) {
          .mentorship-stats-row {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .mentorship-stats-card {
            min-width: 0;
            padding: 12px;
            gap: 8px;
            border-radius: 16px;
          }
          .mentorship-stats-icon {
            width: 36px;
            height: 36px;
          }
        }
      `}</style>

      {!dataReady
        ? STAT_CARDS.map((stat) => <StatCardShimmer key={stat.id} />)
        : STAT_CARDS.map((stat) => {
            const Icon = stat.icon;
            const isMenteesCard = stat.key === "mentees";
            const requestedTab = STAT_TAB_TARGET[stat.key];
            const handleClick = isMenteesCard
              ? () => setIsMenteesModalOpen(true)
              : requestedTab
                ? () =>
                    window.dispatchEvent(
                      new CustomEvent("mrMentorshipTabRequested", {
                        detail: { tab: requestedTab },
                      }),
                    )
                : undefined;
            return (
              <div
                key={stat.id}
                onClick={handleClick}
                className="mentorship-stats-card d-flex align-items-center bg-white flex-grow-1"
                style={handleClick ? { cursor: "pointer" } : undefined}
              >
                <div
                  className="mentorship-stats-icon d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{ background: "rgba(29,193,221,0.12)" }}
                >
                  {Icon ? <Icon color="#1DC1DD" size={20} /> : <img src={stat.iconSrc} alt="" width={20} height={20} />}
                </div>
                <div>
                  <h5 className="fs-20 roboto-bold mb-0">{stats[stat.key]}</h5>
                  <p className="fs-14 mb-0" style={{ fontWeight: "400", color: "#848798" }}>{statLabels[stat.key] || stat.label}</p>
                </div>
              </div>
            );
          })}

      <MentorMenteesModal
        isOpen={isMenteesModalOpen}
        toggle={() => setIsMenteesModalOpen((prev) => !prev)}
      />
    </div>
  );
}

export default React.memo(MentorshipStats);
